import { Link } from '@tanstack/react-router';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { getButtonStyles } from '@sc/shared-ui';
import env from '../environment';
import { openCookieSettings } from '../consent';
import { featurePages } from '../assets/data/features';
import { useCaseData } from '../assets/data/useCases';

const MainFooter = () => (
  <footer className="border-t border-grey-t-800 py-25 px-10" role="contentinfo">
    <div className="max-w-315 mx-auto flex flex-col lg:flex-row gap-15 items-start justify-between">
      {/* Left: brand + description + CTA */}
      <div className="flex flex-col gap-8 lg:max-w-130 items-start">
        <img
          src="/lock-up.svg"
          alt="Stark Crawl"
          width="146"
          height="20"
          className="h-5 w-auto"
        />
        <p className="text-base leading-6 text-grey-200">
          Crawl the web, extract structured data, and capture prospects for
          market research — from discovery to export, without building your own
          scraping stack.
        </p>
        <div className="flex items-center gap-5 flex-wrap">
          <a
            href={env.appUrl + '/register'}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Try Stark Crawl for free (opens in new tab)"
            {...getButtonStyles({
              type: 'primary',
              size: 'large',
              additionalClassnames: 'gap-x-2 w-fit!',
            })}
          >
            Try for free
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
        <small className="text-grey-200 text-xs leding-1.2">
          &copy; All rights reserved, 2026. Stark Crawl is a trading name of{' '}
          <a
            href="https://aduro.io/solutions/stark"
            className="underline text-primary-400"
            target="_blank"
            rel="noreferrer noopener"
          >
            Aduro
          </a>
          , registered in England and Wales. Company number 09838353. Registered
          office 27 Old Gloucester Street, London, WC1N 3AX. VAT number
          357068871.
        </small>
      </div>

      {/* Right: navigation columns */}
      <div className="flex flex-wrap gap-x-15 gap-y-10 items-start">
        <nav aria-label="Why Stark Crawl navigation">
          <h3 className="font-grotesque text-[1.375rem] leading-7 font-semibold text-white mb-3">
            Why Stark Crawl
          </h3>
          <ul className="flex flex-col" role="list">
            {featurePages.map((f) => (
              <li key={f.slug}>
                <Link
                  to={`/${f.slug}`}
                  className="font-open text-sm leading-5 text-grey-300 hover:text-white py-3.5 block"
                >
                  {f.shortTitle}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* What it's for */}
        <nav aria-label="What it's for navigation">
          <h3 className="font-grotesque text-[1.375rem] leading-7 font-semibold text-white mb-3">
            What it&apos;s for
          </h3>
          <ul className="flex flex-col" role="list">
            {useCaseData.useCases.map((useCase) => (
              <li key={useCase.path}>
                <Link
                  to="/use-cases/$slug"
                  params={{ slug: useCase.path }}
                  className="font-open text-sm leading-5 text-grey-300 hover:text-white py-3.5 block"
                >
                  {useCase.headTerm}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Menu */}
        <nav aria-label="Menu navigation">
          <h3 className="font-grotesque text-[1.375rem] leading-7 font-semibold text-white mb-3">
            Menu
          </h3>
          <ul className="flex flex-col" role="list">
            {[
              { to: '/web-crawling-for-research', label: 'What is web crawling for research?' },
              { to: '/pricing', label: 'Pricing' },
              { to: '/blog', label: 'Blog' },
              { to: '/contact', label: 'Contact' },
            ].map(({ to, label }) => (
              <li key={label}>
                <Link
                  to={to}
                  className="font-open text-sm leading-5 text-grey-300 hover:text-white py-3.5 block"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Other links */}
        <nav aria-label="Other links">
          <h3 className="font-grotesque text-[1.375rem] leading-7 font-semibold text-white mb-3">
            Other links
          </h3>
          <ul className="flex flex-col" role="list">
            <li>
              <Link
                to="/privacy"
                className="font-open text-sm leading-5 text-grey-300 hover:text-white py-3.5 block"
              >
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link
                to="/terms"
                className="font-open text-sm leading-5 text-grey-300 hover:text-white py-3.5 block"
              >
                Terms &amp; conditions
              </Link>
            </li>
            <li>
              <button
                type="button"
                onClick={openCookieSettings}
                className="font-open text-sm leading-5 text-grey-300 hover:text-white py-3.5 block cursor-pointer"
              >
                Cookie settings
              </button>
            </li>
            <li>
              <a
                href={env.appUrl + '/login'}
                target="_blank"
                rel="noreferrer noopener"
                className="font-open text-sm leading-5 text-grey-300 hover:text-white py-3.5 block"
              >
                Log in
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </footer>
);

export default MainFooter;
